import { capitalize } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useInfiniteHits, useInstantSearch } from 'react-instantsearch';

import { TalkGrid } from 'components/@tedui';
import { useDidMount } from 'lib/hooks/useDidMount';

import { LazyLoadAd, TalkIndexAds } from 'components/ads';
import { useRouter } from 'next/router';
import { FILTER_BAR_CONSTANTS } from '../FilterBar/Dropdowns';
import { useTrackClickEvent } from '../analytics';
import { RefinementBar } from './Refinements';
import type { SearchViewProps } from './SearchView.props';
import { TopicsLaunchPad } from './TopicsLaunchPad';
export function SearchView({
  talks,
  ...props
}: SearchViewProps): React.ReactNode {
  const hasMounted = useDidMount();
  const { status, indexUiState } = useInstantSearch(props);
  const { hits, results, isLastPage, showMore } = useInfiniteHits(props);

  const router = useRouter();
  const queryObject = router.query;
  const isSearching = Object.keys(queryObject).includes('sort');
  const remainingHits = results?.nbHits ? results.nbHits - hits.length : 0;
  const buttonText = `Show ${
    remainingHits < (results?.hitsPerPage || 0)
      ? remainingHits
      : results?.hitsPerPage
  } more`;

  const isLoading = status === 'stalled' || status === 'loading';

  const trackClick = useTrackClickEvent(
    results
      ? {
          index: results.index,
          nbHits: results.nbHits
        }
      : undefined
  );

  const handlePaginationButtonClick = useCallback(() => {
    trackClick({
      eventName: 'show_more'
    });

    showMore();
  }, [showMore, trackClick]);

  const hasActiveRefinements = uiState => {
    return (
      uiState.query ||
      uiState.sortBy ||
      (uiState.menu && Object.keys(uiState.menu).length > 0) ||
      (uiState.refinementList &&
        Object.keys(uiState.refinementList).length > 0) ||
      (uiState.numericMenu && Object.keys(uiState.numericMenu).length > 0)
    );
  };

  const resultsView = hasMounted && hasActiveRefinements(indexUiState);

  const pagination = resultsView
    ? {
        totalItems: results?.nbHits,
        visibleItems: hits?.length,
        showLoadMoreButton: !isLastPage,
        isLoadingMoreItems: isLoading,
        showCount: true,
        buttonOptions: {
          disabled: false,
          testId: 'Show More Button',
          text: buttonText,
          onClick: handlePaginationButtonClick
        }
      }
    : {
        totalItems: talks?.length,
        visibleItems: 24,
        showLoadMoreButton: talks?.length > 24,
        isLoadingMoreItems: false,
        showCount: false,
        buttonOptions: {
          disabled: false,
          testId: 'Show More Button',
          text: 'Show more'
        }
      };

  const talksToDisplay = useMemo(
    () =>
      resultsView
        ? hits.map(hit => ({
            id: hit.objectID,
            title: hit.title as string,
            duration: hit.duration as number,
            presenterDisplayName: hit.speakers as string,
            primaryImageSet: hit.photos[0]?.photo_sizes.filter(
              size =>
                size.talkstar_aspect_ratio_id === 2 ||
                size.talkstar_aspect_ratio_id === 3
            ),
            slug: hit.slug as string,
            // eslint-disable-next-line no-underscore-dangle
            __position: hit.__position as number,
            updated_at: hit.updated_at as string
          }))
        : talks,
    [hits, resultsView, talks]
  );

  const title = resultsView
    ? `Sorted by ${capitalize(
        FILTER_BAR_CONSTANTS.sortByOptions.find(
          option => option.value === indexUiState.sortBy
        )?.label
      )}`
    : 'Newest Talks';

  return (
    <>
      <TopicsLaunchPad
        position="Top"
        page={resultsView ? 'Results' : 'Default'}
        isOnResultsPage={!!resultsView}
      />
      {hasMounted && resultsView && <RefinementBar />}
      {!isSearching && (
        <LazyLoadAd>
          <TalkIndexAds.TalkIndexAdInlineATF />
        </LazyLoadAd>
      )}
      <TalkGrid
        isOnResultsPage={!!resultsView}
        talks={talksToDisplay}
        title={title}
        pagination={pagination}
      />
      <TopicsLaunchPad
        position="Bottom"
        page="Default"
        isOnResultsPage={!!resultsView}
      />
    </>
  );
}
